import { useState, useEffect, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import api from '../../lib/axios'
import { DEFAULT_SETTINGS } from '../../lib/pageDesign'
import PageRenderer, { HEADER_DEFAULTS } from '../../components/design/PageRenderer'

export default function PagePublic({ previewData = null }) {
  const { slug: routeSlug } = useParams()

  // Preview dans l'éditeur ou page publique réelle.
  const isPreview = !!previewData
  const slug = previewData?.slug || routeSlug

  const [page, setPage] = useState(previewData?.page || null)
  const [blocks, setBlocks] = useState(previewData?.blocks || [])
  const [loading, setLoading] = useState(!isPreview)
  const [notFound, setNotFound] = useState(false)

  /*
   * Chargement des données de la page publique.
   * useCallback stabilise la fonction pour useEffect.
   */
  const load = useCallback(async () => {
    if (!routeSlug) {
      setNotFound(true)
      setLoading(false)
      return
    }

    setLoading(true)
    setNotFound(false)

    try {
      const { data } = await api.get(`/public/pages/${routeSlug}`)
      const p = data.data
      const saved = p.settings || {}

      setPage({
        ...p,
        settings: {
          ...DEFAULT_SETTINGS,
          ...HEADER_DEFAULTS,
          ...saved,
        },
      })

      setBlocks(
        Array.isArray(saved.blocks)
          ? saved.blocks
          : (p.links || []).map((l) => ({
              id: `link-${l.id}`,
              dbId: l.id,
              type: 'link',
              content: {
                title: l.title,
                url: l.url,
                icon: l.icon || 'website',
              },
            }))
      )
    } catch (err) {
      console.error('Erreur chargement page publique:', err)
      setNotFound(true)
    } finally {
      setLoading(false)
    }
  }, [routeSlug])

  /*
   * MODE PREVIEW
   * Les données existent déjà dans l'éditeur.
   * Aucun appel API n'est nécessaire.
   */
  useEffect(() => {
    if (!isPreview) return

    const p = previewData.page || {}
    const saved = p.settings || {}

    setPage({
      ...p,
      settings: {
        ...DEFAULT_SETTINGS,
        ...HEADER_DEFAULTS,
        ...saved,
      },
    })

    setBlocks(
      Array.isArray(previewData.blocks)
        ? previewData.blocks
        : []
    )

    setLoading(false)
    setNotFound(false)
  }, [isPreview, previewData])

  /*
   * MODE PUBLIC
   * Chargement depuis Laravel.
   */
  useEffect(() => {
    if (isPreview) return

    load()
  }, [isPreview, load])

  /*
   * Clic sur un lien.
   * En preview, aucun clic n'est enregistré.
   */
  const onLinkClick = useCallback(
    (block) => {
      if (isPreview || !routeSlug) return

      api
        .post(`/public/pages/${routeSlug}/click`, {
          blockId: block.id,
        })
        .catch(() => {})
    },
    [isPreview, routeSlug]
  )

  /*
   * Écran de chargement.
   * min-h-dvh : hauteur réelle de l'écran sur mobile (la barre d'adresse
   * de Safari / Chrome ne coupe plus le contenu).
   */
  if (loading) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-[#f9f9ff]">
        <Loader2 className="w-8 h-8 text-[#006b5f] animate-spin" />
      </div>
    )
  }

  /*
   * Page introuvable en mode public.
   */
  if (!isPreview && (notFound || !page)) {
    return (
      <div className="min-h-dvh flex flex-col items-center justify-center bg-[#f9f9ff] text-center px-6">
        <h1 className="text-xl font-bold text-navy mb-2">
          Page introuvable
        </h1>

        <p className="text-sm text-slate-500">
          Cette page n'existe pas ou n'est plus publiée.
        </p>
      </div>
    )
  }

  /*
   * Sécurité supplémentaire.
   */
  if (!page) {
    return null
  }

  return (
    <>
      <PageMeta page={page} settings={page.settings} />

      <PageRenderer
        page={page}
        blocks={blocks}
        slug={slug}
        onLinkClick={onLinkClick}
      />
    </>
  )
}

/*
 * ============================================
 * SEO / OPEN GRAPH
 * ============================================
 */

function PageMeta({ page, settings }) {
  useEffect(() => {
    const title =
      settings.seoTitle ||
      page.title ||
      'Tapio'

    const description =
      settings.seoDescription ||
      page.bio ||
      ''

    const image =
      settings.seoImage ||
      settings.coverImage ||
      page.avatar ||
      ''

    document.title = title

    const setMeta = (attr, key, content) => {
      let el = document.querySelector(
        `meta[${attr}="${key}"]`
      )

      if (!el) {
        el = document.createElement('meta')
        el.setAttribute(attr, key)
        document.head.appendChild(el)
      }

      el.setAttribute('content', content || '')
    }

    setMeta('name', 'description', description)
    setMeta('property', 'og:title', title)
    setMeta('property', 'og:description', description)

    if (image) {
      setMeta('property', 'og:image', image)
    }

    setMeta(
      'name',
      'twitter:card',
      image ? 'summary_large_image' : 'summary'
    )
  }, [page, settings])

  return null
}